/**
 * Review and apply (design board 5): the statements the staged changes would run (asked of the server with
 * `apply: false`), what else they do (cascades), then Apply — one transaction, the rows kept for Undo.
 * Also: the sentence Activity records ("Record in Activity as …", editable; unchecked: the server's default summary),
 * the Redis keys that may cache the changed rows ("Clear that key too", after Apply), and for a CSV import the rows
 * that would fail, checked before Apply (each in a savepoint, nothing kept) with "Leave them out".
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Copy } from '@phosphor-icons/react';
import { DataDialog, Loading, errorText } from './DataCommon';
import { applyChanges, checkChanges, clearRedisKeys, previewChanges, redisHint } from '../../services/DataService';
import { formatCount, kindLabel, serviceLabel } from '../../utils/dataModel';
import { describeChanges, hintTargets } from '../../utils/dataImport';

const TAGS = { update: ['UPDATE', 'is-update'], insert: ['INSERT', 'is-insert'], delete: ['DELETE', 'is-delete'] };
const SHOWN = 200;

const minutes = (ttl) => (ttl >= 120 ? `${Math.round(ttl / 60)} minutes` : ttl >= 60 ? '1 minute' : `${ttl} seconds`);

function CacheHint({ keys, clear, onToggle }) {
  const ttl = Math.max(0, ...keys.map((k) => k.ttl || 0));
  const first = keys[0].key;
  const more = keys.length - 1;
  return (
    <li data-testid="review-redis-hint">
      <span className="data-dot is-orange" />
      <span>
        The backend may cache <span className="data-mono">{first}</span>{more > 0 ? ` and ${more} more key${more === 1 ? '' : 's'}` : ''} in Redis
        {ttl ? ` for up to ${minutes(ttl)}` : ''}.{' '}
        <button aria-pressed={clear} className="data-btn is-ghost is-link" data-testid="review-redis-clear" onClick={onToggle} title={keys.map((k) => k.key).join('\n')} type="button">
          {clear ? `Will clear ${more ? 'them' : 'it'} after applying (undo)` : `Clear ${more ? 'those keys' : 'that key'} too`}
        </button>
      </span>
    </li>
  );
}

CacheHint.propTypes = { clear: PropTypes.bool, keys: PropTypes.array.isRequired, onToggle: PropTypes.func.isRequired };

export default function ReviewDialog({
  projectId, store, target, table = null, changes, stores = [], importInfo = null, sources = null, onClose, onApplied, onDropFailing = null,
}) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [record, setRecord] = useState(true);
  const proposed = useMemo(() => describeChanges(changes, { table: table || target, importedFrom: importInfo?.file, importedCount: importInfo?.count || 0 }), [changes, table, target, importInfo]);
  const [own, setOwn] = useState(null); // the person's words, once they type; else the sentence from the changes
  const summary = own ?? proposed;
  const [hint, setHint] = useState(null); // { keys }
  const [clearKeys, setClearKeys] = useState(false);
  const [check, setCheck] = useState(importInfo ? { busy: true } : null);
  const n = changes.length;
  const hasRedis = stores.some((s) => s.kind === 'redis' && s.status === 'running');

  useEffect(() => {
    let live = true;
    previewChanges(projectId, store.id, changes)
      .then((answer) => { if (live) { setPreview(answer); } })
      .catch((e) => { if (live) { setError(errorText(e)); } });
    return () => { live = false; };
  }, [projectId, store.id, changes]);

  useEffect(() => {
    if (!hasRedis || !table) { return undefined; }
    let live = true;
    Promise.resolve().then(() => redisHint(projectId, hintTargets(changes, table)))
      .then((answer) => { if (live && answer?.keys?.length) { setHint({ keys: answer.keys }); } })
      .catch(() => { /* no hint: nothing to say */ });
    return () => { live = false; };
  }, [projectId, hasRedis, table, changes]);

  useEffect(() => {
    if (!importInfo) { return undefined; }
    let live = true;
    setCheck({ busy: true });
    Promise.resolve().then(() => checkChanges(projectId, store.id, changes))
      .then((answer) => { if (live) { setCheck({ busy: false, errors: answer?.errors || [], more: Boolean(answer?.more) }); } })
      .catch((e) => { if (live) { setCheck({ busy: false, failed: errorText(e) }); } });
    return () => { live = false; };
  }, [projectId, store.id, changes, importInfo]);

  const statements = preview?.statements || [];
  const sqlText = statements.map((s) => `${s.sql.trim().replace(/;?$/, ';')}${s.note ? `   -- ${s.note}` : ''}`).join('\n\n');
  const lineOf = (index) => sources?.[index]?.line;

  const apply = async () => {
    setBusy(true);
    setApplyError(null);
    try {
      const own = record ? summary.trim() : '';
      const answer = await applyChanges(projectId, store.id, changes, { summary: own || undefined });
      let cleared = null;
      if (clearKeys && hint?.keys?.length) {
        cleared = await clearRedisKeys(projectId, hint.keys.map((k) => k.key), `after changing ${table}`).catch((e) => ({ failed: errorText(e) }));
      }
      onApplied({ ...answer, ...(own ? { recorded: own } : {}), ...(cleared ? { cleared } : {}) });
    } catch (e) {
      const index = Number.isInteger(e?.body?.index) ? e.body.index : Number.isInteger(e?.body?.statement) ? e.body.statement : null;
      setApplyError({ index, message: errorText(e) });
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sqlText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* no clipboard: the text is on screen */ }
  };

  const services = (store.services || []).map(serviceLabel);
  const subtitle = (
    <>
      {kindLabel(store.kind)} in your sandbox
      {services.length > 0 && <> · used by the <strong>{services.join(', ')}</strong> service{services.length === 1 ? '' : 's'}</>}
    </>
  );
  const failing = check?.errors || [];
  const blocked = Boolean(importInfo) && (check?.busy || failing.length > 0);

  return (
    <DataDialog
      footer={(
        <>
          <button className="data-btn" disabled={!statements.length} onClick={copy} type="button">
            <Copy aria-hidden size={15} />{copied ? 'Copied' : 'Copy SQL'}
          </button>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Back to editing</button>
          <button className="data-btn is-primary" data-testid="review-apply" disabled={busy || !preview || Boolean(error) || blocked} onClick={apply} type="button">
            {busy ? 'Applying…' : `Apply ${formatCount(n)} change${n === 1 ? '' : 's'}`}
          </button>
        </>
      )}
      onClose={onClose}
      subtitle={subtitle}
      testId="review-dialog"
      title={`Apply ${formatCount(n)} change${n === 1 ? '' : 's'} to ${target}?`}
      wide
    >
      {!preview && !error && <Loading label="Writing the statements…" />}
      {error && <p className="data-error-text" role="alert">Could not prepare the changes: {error}</p>}
      {importInfo && check && (
        <div className={`data-check${failing.length ? ' is-bad' : ''}`} data-testid="review-check" role="status">
          {check.busy && <Loading label={`Checking the ${formatCount(importInfo.count)} rows from ${importInfo.file}…`} />}
          {check.failed && <p className="data-error-text">Could not check the rows: {check.failed}</p>}
          {!check.busy && !check.failed && !failing.length && <p>All {formatCount(importInfo.count)} rows from {importInfo.file} fit the table.</p>}
          {failing.length > 0 && (
            <>
              <p><strong>{formatCount(failing.length)}{check.more ? '+' : ''} row{failing.length === 1 ? '' : 's'} would fail</strong>: leave {failing.length === 1 ? 'it' : 'them'} out, or go back and fix the file.</p>
              <ul aria-label="Rows that would fail" className="data-check-list">
                {failing.slice(0, 20).map((f) => (
                  <li data-testid="review-row-error" key={f.index}>
                    {lineOf(f.index) ? `Line ${lineOf(f.index)}` : `Change ${f.index + 1}`}: {f.error}
                  </li>
                ))}
                {failing.length > 20 && <li>… and {formatCount(failing.length - 20)} more</li>}
              </ul>
              {onDropFailing && (
                <button className="data-btn" data-testid="review-drop-failing" onClick={() => onDropFailing(failing.map((f) => f.index))} type="button">
                  Leave out the {formatCount(failing.length)} row{failing.length === 1 ? '' : 's'} that fail
                </button>
              )}
            </>
          )}
        </div>
      )}
      {preview && (
        <>
          <ol aria-label="Statements" className="data-statements">
            {statements.slice(0, SHOWN).map((statement, index) => {
              const [tag, cls] = TAGS[statement.op] || [String(statement.op || '').toUpperCase(), ''];
              const failed = applyError?.index === index;
              return (
                <li className={`data-statement${failed ? ' is-failed' : ''}`} data-testid="review-statement" key={index}>
                  <span className={`data-tag ${cls}`}>{tag}</span>
                  <pre>{statement.sql}{statement.note ? `\n-- ${statement.note}` : ''}</pre>
                </li>
              );
            })}
            {statements.length > SHOWN && <li className="data-statement data-muted">… and {formatCount(statements.length - SHOWN)} more statements like these</li>}
          </ol>
          <ul className="data-notes">
            <li><span className="data-dot is-blue" />{n > 1 ? <>All {formatCount(n)} run in <strong>one transaction</strong>: if one fails, none is applied and you see why.</> : <>It runs in <strong>one transaction</strong>: if it fails, nothing changes and you see why.</>}</li>
            <li><span className="data-dot is-green" />The rows as they are now are kept, so you can <strong>Undo</strong> for the next 10 minutes.</li>
            {(preview.effects || []).map((effect) => (
              <li data-testid="review-effect" key={effect}><span className="data-dot is-orange" />{effect}</li>
            ))}
            {hint && <CacheHint clear={clearKeys} keys={hint.keys} onToggle={() => setClearKeys(!clearKeys)} />}
          </ul>
          <div className="data-record">
            <label className="data-record-check">
              <input checked={record} data-testid="review-record" onChange={(e) => setRecord(e.target.checked)} type="checkbox" />
              Record in Activity as
            </label>
            {record ? (
              <>
                <label className="data-sr" htmlFor="data-review-summary">What these changes do, for Activity</label>
                <input
                  className="data-input is-wide"
                  data-testid="review-summary"
                  id="data-review-summary"
                  maxLength={300}
                  onChange={(e) => setOwn(e.target.value)}
                  placeholder={proposed || 'What these changes do'}
                  value={summary}
                />
              </>
            ) : <span className="data-muted">the default summary (e.g. “updated 1 row in {table || target}”)</span>}
          </div>
          {applyError && (
            <p className="data-error-text" role="alert">
              {applyError.index !== null ? `Statement ${applyError.index + 1}${lineOf(applyError.index) ? ` (line ${lineOf(applyError.index)})` : ''} failed: ` : 'It failed: '}{applyError.message}. Nothing was applied.
            </p>
          )}
        </>
      )}
    </DataDialog>
  );
}

ReviewDialog.propTypes = {
  changes: PropTypes.array.isRequired,
  importInfo: PropTypes.shape({ file: PropTypes.string, count: PropTypes.number }),
  onApplied: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDropFailing: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  sources: PropTypes.array,
  store: PropTypes.object.isRequired,
  stores: PropTypes.array,
  table: PropTypes.string,
  target: PropTypes.string.isRequired,
};
